import { useState, useRef } from 'react';
import type { Folder, Conversation, Section } from '@volt/shared';
import { useSession } from '../state/session.js';
import { useUI } from '../state/ui.js';
import { useI18n } from '../i18n/context.js';
import { placeBeside, type DropTarget } from '../lib/sidebarDnd.js';
import { Icon } from './Icon.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { ContextMenu } from './ContextMenu.js';
import { ConversationList } from './ConversationList.js';
import { useSidebarDnd } from './SidebarDnd.js';
import type { MenuItem } from './ContextMenu.js';

interface FolderItemProps {
  folder: Folder;
  conversations: Conversation[];
  allFolders: Folder[];
  section: Section;
  activeId: string | null;
  onSelectConversation: (id: string) => void;
  onRenameFolder: (folder: Folder) => void;
  onDeleteFolder: (folder: Folder) => void;
  /** Reorder: put `folderId` right before / after the folder `targetId`. */
  onReorder: (folderId: string, targetId: string, after: boolean) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function FolderItem({
  folder,
  conversations,
  allFolders,
  section,
  activeId,
  onSelectConversation,
  onRenameFolder,
  onDeleteFolder,
  onReorder,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
}: FolderItemProps) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const dnd = useSidebarDnd();
  // A chat is being dragged over this folder.
  const conversationDragOver = dnd.target?.kind === 'folder' && dnd.target.folderId === folder.id;
  // A folder is being dragged: this one is the lifted one / the insertion mark is next to it.
  const folderDragActive = dnd.drag?.kind === 'folder';
  const isDragSource = folderDragActive && dnd.drag?.id === folder.id;
  const dropMark =
    dnd.target?.kind === 'folder-slot' && dnd.target.id === folder.id
      ? dnd.target.after
        ? ' is-drop-after'
        : ' is-drop-before'
      : '';
  const [pressing, setPressing] = useState(false);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);

  const menuItems: MenuItem[] = [
    { key: 'rename', label: t('menu.rename'), icon: 'edit', onSelect: () => onRenameFolder(folder) },
    { key: 'delete', label: t('menu.delete'), icon: 'trash', danger: true, onSelect: () => onDeleteFolder(folder) },
  ];

  return (
    <div
      className={`folder-item${isDragSource ? ' is-drag-source' : ''}${conversationDragOver ? ' folder-drag-over' : ''}${dropMark}`}
      data-dnd-folder={folder.id}
    >
      <div className="folder-head">
        <button
          type="button"
          className={`folder-toggle${menuPos !== null ? ' is-pressed' : ''}${pressing ? ' is-pressing' : ''}`}
          onPointerDown={(event) => {
            if (menuPos !== null) return;
            // Touch: hold → haptic + menu; keep moving and it becomes a drag (reorder).
            // Mouse: just drag. A tap expands / collapses the folder.
            dnd.startPress(
              event,
              { kind: 'folder', id: folder.id, title: folder.name },
              {
                onPressChange: setPressing,
                onMenu: setMenuPos,
                // Moving on while the menu is open turns the hold into a drag.
                onMenuClose: () => setMenuPos(null),
                onDrop: (target: DropTarget) => {
                  if (target.kind === 'folder-slot') onReorder(folder.id, target.id, target.after);
                },
              },
            );
          }}
          onClick={(event) => {
            // The click that follows a drag / press-and-release must not toggle the folder.
            if (dnd.consumeSuppressedClick()) {
              event.preventDefault();
              return;
            }
            setExpanded((value) => !value);
          }}
          onKeyDown={(event) => {
            if (event.altKey && event.key === 'ArrowUp' && canMoveUp) {
              event.preventDefault();
              onMoveUp();
            }
            if (event.altKey && event.key === 'ArrowDown' && canMoveDown) {
              event.preventDefault();
              onMoveDown();
            }
          }}
          onContextMenu={(event) => {
            event.preventDefault();
            // On touch the long-press is the drag gesture; its menu opens on release.
            if (dnd.lastPointerType() !== 'mouse') return;
            setMenuPos({ x: event.clientX, y: event.clientY });
          }}
        >
          <Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={14} />
          <Icon name="folder" size={15} />
          <span className="folder-name">{folder.name}</span>
        </button>
      </div>
      {/* Folders fold up while one is dragged so the order is easy to see. */}
      {expanded && !folderDragActive && (
        <div className="folder-convs">
          <ConversationList
            conversations={conversations}
            folders={allFolders}
            section={section}
            activeId={activeId}
            onSelect={onSelectConversation}
            flatList
            hideEmptyState
          />
        </div>
      )}
      <ContextMenu
        open={menuPos !== null}
        x={menuPos?.x ?? 0}
        y={menuPos?.y ?? 0}
        items={menuItems}
        onClose={() => setMenuPos(null)}
      />
    </div>
  );
}

interface FolderListProps {
  folders: Folder[];
  conversations: Conversation[];
  section: Section;
  activeId: string | null;
  onSelectConversation: (id: string) => void;
  onFoldersChanged: (updater: Folder[] | ((prev: Folder[]) => Folder[])) => void;
}

export function FolderList({
  folders,
  conversations,
  section,
  activeId,
  onSelectConversation,
  onFoldersChanged,
}: FolderListProps) {
  const { t } = useI18n();
  const { api } = useSession();
  const { toast, refreshFolders, refreshConversations } = useUI();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState<Folder | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleting, setDeleting] = useState<Folder | null>(null);
  const creatingRef = useRef(false);
  // Submit and blur both finish a rename: only the first one goes to the server.
  const renamingRef = useRef(false);
  // Reorders are saved one after another; a new one waits for the previous.
  const reorderQueueRef = useRef<Promise<void>>(Promise.resolve());

  /** Move `sourceId` right before / after `targetId`, show it at once and save the new positions. */
  function moveFolder(sourceId: string, targetId: string, after: boolean) {
    const ids = placeBeside(
      folders.map((folder) => folder.id),
      sourceId,
      targetId,
      after,
    );
    const next = ids
      .map((id) => folders.find((folder) => folder.id === id))
      .filter((folder): folder is Folder => Boolean(folder))
      .map((folder, index) => ({ ...folder, position: index }));
    if (next.every((folder, index) => folder.id === folders[index]?.id)) return;
    const changed = next.filter((folder) => {
      const before = folders.find((item) => item.id === folder.id);
      return before?.position !== folder.position;
    });
    onFoldersChanged(next);
    reorderQueueRef.current = reorderQueueRef.current.then(async () => {
      try {
        // Sequential PATCHes: parallel ones raced each other on the server.
        for (const folder of changed) {
          await api.updateFolder(folder.id, { position: folder.position });
        }
      } catch {
        toast(t('common.error'), 'error');
      } finally {
        // Whatever was saved (all, or only part of it) is now the server's order.
        refreshFolders();
      }
    });
  }

  async function handleCreate() {
    if (creatingRef.current) return;
    const name = newName.trim();
    if (!name) {
      setCreating(false);
      return;
    }
    creatingRef.current = true;
    setCreating(false);
    setNewName('');
    try {
      const { folder } = await api.createFolder({ name });
      onFoldersChanged((prev) => [...prev, folder]);
      refreshFolders();
    } catch {
      toast(t('common.error'), 'error');
      setCreating(true);
      setNewName(name);
    } finally {
      creatingRef.current = false;
    }
  }

  async function handleRename(folder: Folder) {
    if (renamingRef.current) return;
    renamingRef.current = true;
    const name = renameValue.trim();
    setRenaming(null);
    try {
      if (name && name !== folder.name) {
        const { folder: updated } = await api.updateFolder(folder.id, { name });
        onFoldersChanged((prev) => prev.map((f) => (f.id === folder.id ? updated : f)));
        refreshFolders();
      }
    } catch {
      toast(t('common.error'), 'error');
    } finally {
      renamingRef.current = false;
    }
  }

  function cancelRename() {
    renamingRef.current = true; // the blur that follows must not submit
    setRenaming(null);
    window.setTimeout(() => {
      renamingRef.current = false;
    }, 0);
  }

  async function handleDelete(folder: Folder) {
    try {
      await api.deleteFolder(folder.id);
      onFoldersChanged((prev) => prev.filter((f) => f.id !== folder.id));
      refreshFolders();
      // Its chats are unfiled on the server (folder_id -> null): reload them too.
      refreshConversations();
    } catch {
      toast(t('common.error'), 'error');
    }
  }

  if (folders.length === 0 && !creating) {
    return (
      <button
        type="button"
        className="btn btn-ghost folder-new-btn"
        onClick={() => setCreating(true)}
      >
        <Icon name="folderPlus" size={16} />
        {t('sidebar.newFolder')}
      </button>
    );
  }

  const folderConvMap: Record<string, Conversation[]> = {};
  for (const folder of folders) {
    folderConvMap[folder.id] = conversations.filter((c) => c.folderId === folder.id);
  }

  return (
    <div className="folder-list">
      {folders.map((folder) =>
        renaming?.id === folder.id ? (
          <form
            key={folder.id}
            className="folder-rename"
            onSubmit={(e) => {
              e.preventDefault();
              void handleRename(folder);
            }}
          >
            <input
              className="field folder-rename-input"
              value={renameValue}
              autoFocus
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={() => void handleRename(folder)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') return;
                e.preventDefault();
                e.stopPropagation();
                cancelRename();
              }}
            />
          </form>
        ) : (
          <FolderItem
            key={folder.id}
            folder={folder}
            conversations={folderConvMap[folder.id] ?? []}
            allFolders={folders}
            section={section}
            activeId={activeId}
            onSelectConversation={onSelectConversation}
            onRenameFolder={(f) => {
              setRenaming(f);
              setRenameValue(f.name);
            }}
            onDeleteFolder={setDeleting}
            onReorder={moveFolder}
            canMoveUp={folders.findIndex((item) => item.id === folder.id) > 0}
            canMoveDown={folders.findIndex((item) => item.id === folder.id) < folders.length - 1}
            onMoveUp={() => {
              const target = folders[folders.findIndex((item) => item.id === folder.id) - 1];
              if (target) moveFolder(folder.id, target.id, false);
            }}
            onMoveDown={() => {
              const target = folders[folders.findIndex((item) => item.id === folder.id) + 1];
              if (target) moveFolder(folder.id, target.id, true);
            }}
          />
        ),
      )}
      {creating ? (
        <form
          className="folder-create"
          onSubmit={(e) => {
            e.preventDefault();
            void handleCreate();
          }}
        >
          <input
            className="field folder-create-input"
            value={newName}
            autoFocus
            placeholder={t('sidebar.folderName')}
            onChange={(e) => setNewName(e.target.value)}
            onBlur={() => void handleCreate()}
          />
        </form>
      ) : (
        <button
          type="button"
          className="btn btn-ghost folder-new-btn"
          onClick={() => setCreating(true)}
        >
          <Icon name="folderPlus" size={16} />
          {t('sidebar.newFolder')}
        </button>
      )}
      <ConfirmDialog
        open={deleting !== null}
        title={t('sidebar.deleteFolderTitle')}
        message={t('sidebar.deleteFolderConfirm')}
        danger
        onConfirm={() => {
          const folder = deleting;
          setDeleting(null);
          if (folder) void handleDelete(folder);
        }}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
